/**
 * Helper functions for Consolidated Executive Report
 * Used by both HTML Preview and PDF Export
 */

import { FleetReportData, FleetReportAlert } from "@/types/fleet-reports";

// =============================
// Formatters
// =============================

export function formatCurrency(v?: number): string {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatNumber(v?: number): string {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR");
}

export function formatKm(v?: number): string {
  return `${formatNumber(v)} km`;
}

export function formatPercent(v?: number): string {
  return `${(v || 0).toFixed(1)}%`;
}

// =============================
// KPI Badge Logic
// =============================

export type KpiBadgeTone = "crit" | "warn" | "ok";

export interface KpiBadge {
  label: string;
  tone: KpiBadgeTone;
}

export function getKpiBadge(costPerKm?: number): KpiBadge {
  const v = Number(costPerKm || 0);
  if (v >= 2.5) return { label: "Crítico", tone: "crit" };
  if (v >= 1.5) return { label: "Atenção", tone: "warn" };
  return { label: "Ok", tone: "ok" };
}

// =============================
// Alert Sorting
// =============================

export type AlertPriority = "critical" | "high" | "medium" | "low";

export function priorityOrder(p?: string): number {
  switch (p?.toLowerCase()) {
    case "critical": return 1;
    case "high": return 2;
    case "medium": 
    case "warning": return 3;
    case "low":
    case "info": return 4;
    default: return 99;
  }
}

export function sortAlertsByPriority(alerts: FleetReportAlert[]): FleetReportAlert[] {
  return [...alerts].sort((a, b) => 
    priorityOrder(a.severity || a.priority) - priorityOrder(b.severity || b.priority)
  );
}

// =============================
// Conclusion Generator
// =============================

export interface ExecutiveConclusion {
  summary: string;
  recommendations: string[];
}

export function generateExecutiveConclusion(data: FleetReportData): ExecutiveConclusion {
  const k = data.summaryKpis;
  const vehicles = [...(data.byVehicle?.rows || [])].sort(
    (a, b) => (Number(b.kpis?.totalCost) || 0) - (Number(a.kpis?.totalCost) || 0)
  );
  const top3 = vehicles.slice(0, 3);
  const totalCost = Number(k.totalCost || 0);

  const top3Cost = top3.reduce((s, v) => s + Number(v.kpis?.totalCost || 0), 0);
  const top3Pct = totalCost > 0 ? Math.round((top3Cost / totalCost) * 100) : 0;

  const activeVehicles = Number(k.activeVehicles || k.totalVehicles || vehicles.length);
  const totalKm = Number(k.totalKm || 0);
  const criticalAlerts = Number(k.criticalAlerts || 0);

  let summary = `A frota apresenta ${activeVehicles} veículos ativos, com custo total de ${formatCurrency(totalCost)} ` +
    `e quilometragem de ${formatKm(totalKm)} no período analisado.`;
  
  if (top3Pct > 0) {
    summary += ` Os 3 veículos de maior custo concentram aproximadamente ${top3Pct}% do total.`;
  }
  
  if (criticalAlerts > 0) {
    summary += ` Existem ${criticalAlerts} alertas críticos que exigem ação imediata.`;
  } else {
    summary += ` Não há alertas críticos ativos no período.`;
  }

  const recommendations: string[] = [];
  
  if (top3.length > 0) {
    const topVehicles = top3.map(v => v.vehicle_label?.split(" - ")[0] || v.vehicle_id).join(", ");
    recommendations.push(`Priorizar revisão de custo/eficiência nos veículos: ${topVehicles}.`);
  }
  
  const fuelPercent = Number(k.fuelPercent || 0);
  if (fuelPercent > 60) {
    recommendations.push("Implementar controle de combustível (auditoria de abastecimento + metas por rota).");
  }
  
  const costPerKm = Number(k.costPerKm || 0);
  if (costPerKm > 2) {
    recommendations.push("Auditar custo/km (manutenção corretiva, consumo fora do padrão e rotas).");
  }
  
  const expiredDocuments = Number(k.expiredDocuments || 0);
  if (expiredDocuments > 0) {
    recommendations.push("Regularizar documentos vencidos para reduzir risco legal e operacional.");
  }
  
  if (criticalAlerts > 0) {
    recommendations.push("Tratar alertas críticos antes de operações longas/rotas críticas.");
  }
  
  recommendations.push("Acompanhar semanalmente custo/km e % combustível vs manutenção para detectar tendência.");

  return { summary, recommendations };
}

// =============================
// Insights Generator (fallback)
// =============================

export function generateInsights(data: FleetReportData): string[] {
  // Se já tem insights da RPC, retornar eles
  if (data.insights && data.insights.length > 0) {
    return data.insights;
  }

  const k = data.summaryKpis;
  const insights: string[] = [];
  
  const vehicles = [...(data.byVehicle?.rows || [])].sort(
    (a, b) => (Number(b.kpis?.totalCost) || 0) - (Number(a.kpis?.totalCost) || 0)
  );
  const top3 = vehicles.slice(0, 3);
  const totalCost = Number(k.totalCost || 0);
  
  if (top3.length >= 3 && totalCost > 0) {
    const top3Cost = top3.reduce((s, v) => s + Number(v.kpis?.totalCost || 0), 0);
    const top3Pct = Math.round((top3Cost / totalCost) * 100);
    insights.push(`Top 3 veículos concentram ${top3Pct}% do custo total do período — priorize ação nesses ativos.`);
  }
  
  // Maior custo/km
  const highestCostPerKm = vehicles.reduce((max, v) => {
    const cost = Number(v.kpis?.costPerKm || 0);
    return cost > max.cost ? { vehicle: v.vehicle_label, cost } : max;
  }, { vehicle: "", cost: 0 });
  
  if (highestCostPerKm.cost > 0) {
    insights.push(`Maior custo/km: ${highestCostPerKm.vehicle?.split(" - ")[0]} — ${formatCurrency(highestCostPerKm.cost)}/km. Recomenda-se revisar consumo, rotas e manutenção corretiva.`);
  }
  
  const fuelPercent = Number(k.fuelPercent || 0);
  if (fuelPercent > 50) {
    insights.push(`Combustível representa ${fuelPercent.toFixed(0)}% do custo total — oportunidade imediata para controles de abastecimento.`);
  }
  
  const criticalAlerts = Number(k.criticalAlerts || 0);
  if (criticalAlerts > 0) {
    insights.push(`${criticalAlerts} alertas críticos ativos: risco operacional imediato. Prioridade: tratar antes de operações longas.`);
  }
  
  const expiredDocuments = Number(k.expiredDocuments || 0);
  if (expiredDocuments > 0) {
    insights.push(`${expiredDocuments} documentos vencidos: risco de multa/paralisação. Regularizar e ativar alertas de vencimento.`);
  }

  return insights;
}
